import React, { useEffect, useState, useRef, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useAtom } from 'jotai';
import { powerAtom } from '../atoms/power';
import { CircularProgress } from '@mui/material';
import { BuildingIDProps } from '../atoms/chargers';
import { DateTime } from 'luxon';
import { calculateTimeIntervals, formatTimeLabel, shouldShowTimeLabel, toEasternDateTime } from '../converters/timespanConverter';

interface PowerGraphProps extends BuildingIDProps {
  startDateTime: DateTime;
  endDateTime: DateTime;
  isLoading?: boolean;
  forceNoDataOnEmpty?: boolean;
}

const PowerGraph: React.FC<PowerGraphProps> = ({ BuildingID, startDateTime, endDateTime, isLoading, forceNoDataOnEmpty }) => {
  const [powerDataMap] = useAtom(powerAtom);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [legendSelected, setLegendSelected] = useState<Record<string, boolean>>({
    'Grid': true,
    'Load': true,
    'Solar': true,
    'Demand': true,
    'DayPeak': true,
    'NCharging': true,
    'NWaiting': true,
  });
  const chartRef = useRef<ReactECharts>(null);

  const powerData = useMemo(
    () => (
      BuildingID && Array.isArray(powerDataMap[BuildingID.toLowerCase()])
        ? powerDataMap[BuildingID.toLowerCase()]
        : []
    ),
    [BuildingID, powerDataMap]
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
      if (chartRef.current) {
        chartRef.current.getEchartsInstance().resize();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (powerData && powerData.length > 0 && chartRef.current) {
      const timer = setTimeout(() => {
        if (chartRef.current) chartRef.current.getEchartsInstance().resize();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [powerData]);

  useEffect(() => {
    if (!chartRef.current) return;
    const echartsInstance = chartRef.current.getEchartsInstance();

    if (!powerData || powerData.length === 0) {
      echartsInstance.clear();
      return;
    }

    const Grid = powerData.map(b => b.Grid ?? 0);
    const Load = powerData.map(b => b.Load ?? 0);
    const Solar = powerData.map(b => b.Solar ?? 0);
    const Demand = powerData.map(b => b.Demand ?? 0);
    const DayPeak = powerData.map(b => b.DayPeak ?? 0);
    const NCharging = powerData.map(b => b.NCharging ?? 0);
    const NWaiting = powerData.map(b => b.NWaiting ?? 0);

    echartsInstance.setOption(
      {
        series: [
          { data: Grid }, { data: Load }, { data: Solar }, { data: Demand },
          { data: DayPeak }, { data: NCharging }, { data: NWaiting },
        ],
        legend: { selected: legendSelected },
      },
      false
    );

    setTimeout(() => echartsInstance.resize(), 0);
  }, [powerData, legendSelected]);

  if (!powerData) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
        <CircularProgress style={{ color: 'firebrick' }} />
        <h3 style={{ color: 'text.primary', marginTop: '20px' }}>Waiting for data...</h3>
      </div>
    );
  }

  if ((forceNoDataOnEmpty || !isLoading) && Array.isArray(powerData) && powerData.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
        <h3 style={{ color: 'grey' }}>No data for selected range</h3>
      </div>
    );
  }

  const xAxisData: string[] = [];
  const verticalLines: any[] = [];
  let previousDate = '';
  const { interval, showHalfHours } = calculateTimeIntervals(startDateTime, endDateTime);

  powerData.forEach((b, index) => {
    const timestamp = toEasternDateTime(b);
    const formattedTime = formatTimeLabel(timestamp, showHalfHours);
    const currentDate = timestamp.toFormat('M/d');
    xAxisData.push(formattedTime);

    if (currentDate !== previousDate && previousDate !== '') {
      verticalLines.push({
        xAxis: index,
        lineStyle: { color: 'lightgrey', type: 'dashed' },
        label: { formatter: currentDate, position: 'end', fontSize: 10, color: 'grey' },
        tooltip: { formatter: `Date: ${currentDate}` },
      });
    }
    previousDate = currentDate;
  });

  const Grid = powerData.map(b => b.Grid ?? 0);
  const Load = powerData.map(b => b.Load ?? 0);
  const Solar = powerData.map(b => b.Solar ?? 0);
  const Demand = powerData.map(b => b.Demand ?? 0);
  const DayPeak = powerData.map(b => b.DayPeak ?? 0);
  const NCharging = powerData.map(b => b.NCharging ?? 0);
  const NWaiting = powerData.map(b => b.NWaiting ?? 0);

  const option = {
    backgroundColor: 'transparent',
    animation: true,
    animationDuration: 300,
    animationEasing: 'cubicOut',
    tooltip: {
      show: !isMobile,
      trigger: 'axis',
      axisPointer: { type: 'line' },
      formatter: function (params: any) {
        const timestamp = toEasternDateTime(powerData[params[0].dataIndex]);
        const formattedTime = timestamp.toFormat('MM/dd/yyyy h:mm:ss a');
        let result = `${formattedTime}<br/>`;
        params.forEach((param: any) => {
          const value = param.value === null || param.value === undefined ? 'No data' : Number(param.value).toFixed(4);
          result += `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>${param.seriesName}: ${value}<br/>`;
        });
        return result;
      },
    },
    legend: {
      data: ['Grid', 'Load', 'Solar', 'Demand', 'DayPeak', 'NCharging', 'NWaiting'],
      textStyle: { color: 'grey' },
      bottom: 0,
      padding: [5, 0, 0, 0],
      selected: legendSelected,
      selector: [
        { type: 'all', title: 'View All' },
        { type: 'inverse', title: 'All Off' },
      ],
      selectorPosition: 'end',
      selectorLabel: { color: 'grey' },
    },
    grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
    toolbox: { feature: { saveAsImage: {} } },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: xAxisData,
      axisLabel: {
        color: 'grey',
        rotate: 45,
        interval: (index: number) => {
          const timestamp = toEasternDateTime(powerData[index]);
          return shouldShowTimeLabel(timestamp, startDateTime, interval, showHalfHours);
        },
        formatter: (value: string) => {
          const timestamp = toEasternDateTime(powerData[xAxisData.indexOf(value)]);
          return formatTimeLabel(timestamp, showHalfHours);
        },
      },
    },
    yAxis: { type: 'value', axisLabel: { color: 'grey' } },
    series: [
      { name: 'Grid', type: 'line', data: Grid, lineStyle: { color: 'firebrick' }, itemStyle: { color: 'firebrick' },
        markLine: { symbol: 'none', data: verticalLines } },
      { name: 'Load', type: 'line', data: Load, lineStyle: { color: 'steelblue' }, itemStyle: { color: 'steelblue' } },
      { name: 'Solar', type: 'line', data: Solar, lineStyle: { color: 'orange' }, itemStyle: { color: 'orange' } },
      { name: 'Demand', type: 'line', data: Demand, lineStyle: { color: 'slateblue' }, itemStyle: { color: 'slateblue' } },
      { name: 'DayPeak', type: 'line', data: DayPeak, lineStyle: { color: 'seagreen' }, itemStyle: { color: 'seagreen' } },
      { name: 'NCharging', type: 'line', data: NCharging, lineStyle: { color: 'purple' }, itemStyle: { color: 'purple' } },
      { name: 'NWaiting', type: 'line', data: NWaiting, lineStyle: { color: 'lightsalmon' }, itemStyle: { color: 'lightsalmon' } },
    ],
  };

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%', minHeight: '400px' }}>
      <ReactECharts
        ref={chartRef}
        option={option}
        className="cancelSelectorName"
        style={{ height: '100%', width: '100%', minHeight: '400px' }}
        opts={{ renderer: 'canvas' }}
        onEvents={{
          legendselectchanged: (e: any) => { if (e?.selected) setLegendSelected(e.selected); },
          legendinverseselect: (e: any) => { if (e?.selected) setLegendSelected(e.selected); },
          legendselectall: (e: any) => { if (e?.selected) setLegendSelected(e.selected); },
        }}
        notMerge={false}
      />
    </div>
  );
};

export default PowerGraph;
